import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PhotoManager } from "@/components/admin/PhotoManager";
import {
  MAKES, MAKES_MODELS, TRUCK_CLASSES, CONDITIONS,
  TRANSMISSIONS, FUEL_TYPES, DRIVETRAINS, ENGINE_TYPES,
} from "@/constants/vehicleData";

const inputCls = "bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus-visible:ring-[#FF6B00] rounded-sm h-11";
const selectCls =
  "h-11 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm text-zinc-900 focus:outline-none focus:ring-1 focus:ring-[#FF6B00]";

const EMPTY = {
  title: "", make: "", model: "", trim: "", year: 2024, category: "",
  stock_number: "", vin: "", condition: "Used", status: "available", featured: false,
  price: "", sale_price: "", mileage: "",
  engine: "", engine_type: "", transmission: "Automatic", drivetrain: "4WD",
  fuel_type: "Diesel", truck_class: "", towing_capacity: "", payload: "", payload_lbs: "",
  horsepower: "", torque: "", exterior_color: "", interior_color: "",
  images: [], description: "", featuresText: "",
  seo_title: "", seo_description: "",
};

const toForm = (v) =>
  v
    ? { ...EMPTY, ...v, sale_price: v.sale_price ?? "", payload_lbs: v.payload_lbs ?? "", images: v.images || [], featuresText: (v.features || []).join(", ") }
    : { ...EMPTY, category: "" };

const Field = ({ label, k, type = "text", required = false, testid, form, set }) => (
  <div>
    <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">{label}{required ? " *" : ""}</Label>
    <Input type={type} required={required} value={form[k]} onChange={set(k)} className={inputCls} data-testid={testid} />
  </div>
);

const Select = ({ label, k, options, required = false, testid, placeholder = "Select", form, set }) => (
  <div>
    <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">{label}{required ? " *" : ""}</Label>
    <select required={required} value={form[k]} onChange={set(k)} className={selectCls} data-testid={testid}>
      <option value="">{placeholder}</option>
      {options.map((o) =>
        typeof o === "object" ? (
          <option key={o.value} value={o.value}>{o.label}</option>
        ) : (
          <option key={o} value={o}>{o}</option>
        )
      )}
    </select>
  </div>
);

export const VehicleFormDialog = ({ open, onOpenChange, vehicle, categories = [], onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(toForm(vehicle));
  }, [open, vehicle]);

  const set = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val, ...(k === "make" ? { model: "" } : {}) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title,
      make: form.make,
      model: form.model,
      trim: form.trim,
      year: Number(form.year),
      category: form.category,
      stock_number: form.stock_number,
      vin: form.vin,
      condition: form.condition,
      status: form.status,
      featured: !!form.featured,
      price: Number(form.price),
      sale_price: form.sale_price === "" ? null : Number(form.sale_price),
      mileage: Number(form.mileage),
      engine: form.engine,
      engine_type: form.engine_type,
      transmission: form.transmission,
      drivetrain: form.drivetrain,
      fuel_type: form.fuel_type,
      truck_class: form.truck_class,
      towing_capacity: form.towing_capacity,
      payload: form.payload,
      payload_lbs: form.payload_lbs === "" ? null : Number(form.payload_lbs),
      horsepower: form.horsepower,
      torque: form.torque,
      exterior_color: form.exterior_color,
      interior_color: form.interior_color,
      images: form.images,
      features: form.featuresText.split(",").map((s) => s.trim()).filter(Boolean),
      description: form.description,
      seo_title: form.seo_title,
      seo_description: form.seo_description,
    };
    try {
      if (vehicle?.id) {
        await api.put(`/admin/vehicles/${vehicle.id}`, payload);
        toast.success("Vehicle updated");
      } else {
        await api.post("/admin/vehicles", payload);
        toast.success("Vehicle added to inventory");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const modelOptions = form.make ? MAKES_MODELS[form.make] || [] : [];


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-zinc-200 text-zinc-900 max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="vehicle-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black uppercase text-zinc-900" data-testid="vehicle-form-title">
            {vehicle ? "Edit Vehicle" : "Add Vehicle"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="mt-2">
          <Tabs defaultValue="basic">
            <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm flex-wrap h-auto">
              {[
                ["basic", "Basic Info"], ["pricing", "Pricing"], ["specs", "Specifications"],
                ["photos", "Photos"], ["description", "Description"], ["seo", "SEO"],
              ].map(([v, l]) => (
                <TabsTrigger key={v} value={v} className="data-[state=active]:bg-[#FF6B00] data-[state=active]:text-black rounded-sm uppercase text-[10px] font-bold tracking-wider" data-testid={`vf-tab-${v}`}>
                  {l}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="basic">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                <div className="sm:col-span-2 lg:col-span-3">
                  <Field form={form} set={set} label="Listing Title" k="title" required testid="vf-title-input" />
                </div>
                <Select form={form} set={set} label="Make" k="make" options={MAKES} required testid="vf-make-select" />
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Model *</Label>
                  <select required value={form.model} onChange={set("model")} disabled={!form.make} className={selectCls} data-testid="vf-model-select">
                    <option value="">{form.make ? "Select model" : "Select make first"}</option>
                    {modelOptions.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <Field form={form} set={set} label="Trim" k="trim" testid="vf-trim-input" />
                <Field form={form} set={set} label="Year" k="year" type="number" required testid="vf-year-input" />
                <Select form={form} set={set} label="Category" k="category" options={categories.map((c) => ({ value: c.slug, label: c.title }))} required testid="vf-category-select" />
                <Select form={form} set={set} label="Condition" k="condition" options={CONDITIONS} required testid="vf-condition-select" />
                <Select form={form} set={set} label="Status" k="status" options={["available", "pending", "sold", "draft", "archived"]} required testid="vf-status-select" />
                <Field form={form} set={set} label="Stock Number" k="stock_number" testid="vf-stock-input" />
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
                    <input type="checkbox" checked={form.featured} onChange={set("featured")} className="accent-[#FF6B00] h-4 w-4" data-testid="vf-featured-checkbox" />
                    Featured on homepage
                  </label>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="pricing">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <Field form={form} set={set} label="Price ($)" k="price" type="number" required testid="vf-price-input" />
                <Field form={form} set={set} label="Sale Price ($, optional)" k="sale_price" type="number" testid="vf-sale-price-input" />
                <Field form={form} set={set} label="Mileage (km)" k="mileage" type="number" required testid="vf-mileage-input" />
              </div>
            </TabsContent>

            <TabsContent value="specs">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                <Field form={form} set={set} label="VIN" k="vin" testid="vf-vin-input" />
                <Field form={form} set={set} label="Engine (description)" k="engine" testid="vf-engine-input" />
                <Select form={form} set={set} label="Engine Type" k="engine_type" options={ENGINE_TYPES} testid="vf-engine-type-select" />
                <Select form={form} set={set} label="Transmission" k="transmission" options={TRANSMISSIONS} required testid="vf-transmission-select" />
                <Select form={form} set={set} label="Drivetrain" k="drivetrain" options={DRIVETRAINS} required testid="vf-drivetrain-select" />
                <Select form={form} set={set} label="Fuel Type" k="fuel_type" options={FUEL_TYPES} required testid="vf-fuel-select" />
                <Select form={form} set={set} label="Truck Class" k="truck_class" options={TRUCK_CLASSES} testid="vf-class-select" />
                <Field form={form} set={set} label="Towing Capacity" k="towing_capacity" testid="vf-towing-input" />
                <Field form={form} set={set} label="Payload (display)" k="payload" testid="vf-payload-input" />
                <Field form={form} set={set} label="Payload (lbs, numeric)" k="payload_lbs" type="number" testid="vf-payload-lbs-input" />
                <Field form={form} set={set} label="Horsepower" k="horsepower" testid="vf-hp-input" />
                <Field form={form} set={set} label="Torque" k="torque" testid="vf-torque-input" />
                <Field form={form} set={set} label="Exterior Color" k="exterior_color" testid="vf-ext-color-input" />
                <Field form={form} set={set} label="Interior Color" k="interior_color" testid="vf-int-color-input" />
              </div>
            </TabsContent>

            <TabsContent value="photos">
              <div className="mt-4">
                <PhotoManager images={form.images} onChange={(imgs) => setForm((f) => ({ ...f, images: imgs }))} />
              </div>
            </TabsContent>

            <TabsContent value="description">
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Description</Label>
                  <Textarea value={form.description} onChange={set("description")} className={`${inputCls} min-h-[120px]`} data-testid="vf-description-input" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Features (comma separated)</Label>
                  <Textarea value={form.featuresText} onChange={set("featuresText")} className={`${inputCls} min-h-[80px]`} data-testid="vf-features-input" />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="seo">
              <div className="space-y-4 mt-4">
                <Field form={form} set={set} label="SEO Title (optional)" k="seo_title" testid="vf-seo-title-input" />
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">SEO Description (optional)</Label>
                  <Textarea value={form.seo_description} onChange={set("seo_description")} className={`${inputCls} min-h-[80px]`} data-testid="vf-seo-desc-input" />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex gap-3 mt-6">
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 h-12 bg-[#FF6B00] hover:bg-[#FF8533] text-black font-bold uppercase tracking-wider rounded-sm"
              data-testid="vf-submit-button"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : vehicle ? "Save Changes" : "Add Vehicle"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-zinc-300 text-zinc-600 rounded-sm uppercase text-xs font-bold tracking-wider h-12"
              data-testid="vf-cancel-button"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
